package top.wcpe.allincore.ui.runtime.render.transform

import top.wcpe.allincore.ui.runtime.element.UIElement
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.tan

/**
 * 3×3 行优先仿射矩阵，用于 2D 旋转/倾斜变换。
 * ```
 *   | m00 m01 m02 |
 *   | m10 m11 m12 |
 *   | m20 m21 m22 |
 * ```
 * 纯仿射时第 3 行为 `[0, 0, 1]`。
 *
 * 注：perspective 不在此矩阵中表达——2D 平面上 perspective 不改变坐标，
 * 真正的透视效果由 UiRenderBridge 的 4×4 矩阵升维实现。
 *
 * 用 `@JvmField` 暴露 9 个分量字段，保留原 Java `public final float` 的字段访问 ABI。
 */
// 矩阵系数（单位阵的 0/1、各工厂字面量）是线性代数本征值，非配置型魔法值，整体豁免 MagicNumber。
@Suppress("MagicNumber")
class AffineMatrix3(
    @JvmField val m00: Float,
    @JvmField val m01: Float,
    @JvmField val m02: Float,
    @JvmField val m10: Float,
    @JvmField val m11: Float,
    @JvmField val m12: Float,
    @JvmField val m20: Float,
    @JvmField val m21: Float,
    @JvmField val m22: Float,
) {

    // -------- 矩阵乘法 --------

    /** 矩阵乘法：`this × other`。 */
    fun multiply(o: AffineMatrix3): AffineMatrix3 = AffineMatrix3(
        m00 * o.m00 + m01 * o.m10 + m02 * o.m20,
        m00 * o.m01 + m01 * o.m11 + m02 * o.m21,
        m00 * o.m02 + m01 * o.m12 + m02 * o.m22,
        m10 * o.m00 + m11 * o.m10 + m12 * o.m20,
        m10 * o.m01 + m11 * o.m11 + m12 * o.m21,
        m10 * o.m02 + m11 * o.m12 + m12 * o.m22,
        m20 * o.m00 + m21 * o.m10 + m22 * o.m20,
        m20 * o.m01 + m21 * o.m11 + m22 * o.m21,
        m20 * o.m02 + m21 * o.m12 + m22 * o.m22,
    )

    // -------- 点变换 --------

    /** 变换后 X 坐标（含齐次除法）。 */
    fun applyX(x: Float, y: Float): Float {
        val w = m20 * x + m21 * y + m22
        val numerator = m00 * x + m01 * y + m02
        return if (abs(w) < DETERMINANT_EPSILON) numerator else numerator / w
    }

    /** 变换后 Y 坐标（含齐次除法）。 */
    fun applyY(x: Float, y: Float): Float {
        val w = m20 * x + m21 * y + m22
        val numerator = m10 * x + m11 * y + m12
        return if (abs(w) < DETERMINANT_EPSILON) numerator else numerator / w
    }

    // -------- 求逆 --------

    /**
     * 用伴随矩阵法求逆矩阵。
     *
     * 行列式接近零时视为退化矩阵，返回 [IDENTITY]（数学退化的合理降级）。
     */
    fun invert(): AffineMatrix3 {
        val det = determinant()
        // 行列式接近零：矩阵退化，返回单位矩阵作为安全降级
        if (abs(det) < DETERMINANT_EPSILON) {
            return IDENTITY
        }
        val invDet = 1f / det
        return AffineMatrix3(
            (m11 * m22 - m12 * m21) * invDet,
            (m02 * m21 - m01 * m22) * invDet,
            (m01 * m12 - m02 * m11) * invDet,
            (m12 * m20 - m10 * m22) * invDet,
            (m00 * m22 - m02 * m20) * invDet,
            (m02 * m10 - m00 * m12) * invDet,
            (m10 * m21 - m11 * m20) * invDet,
            (m01 * m20 - m00 * m21) * invDet,
            (m00 * m11 - m01 * m10) * invDet,
        )
    }

    /** 计算行列式。 */
    fun determinant(): Float = m00 * (m11 * m22 - m12 * m21) -
        m01 * (m10 * m22 - m12 * m20) +
        m02 * (m10 * m21 - m11 * m20)

    // -------- 包围盒 / 命中测试 --------

    /**
     * 对矩形 4 个角点做变换，返回轴对齐外接矩形。
     *
     * 返回格式：`[left, top, width, height, |scaleX|, |scaleY|]`，与 [Transform2D.transformedBox]
     * 同布局，便于上层兼容。
     */
    fun transformedAABB(x: Float, y: Float, w: Float, h: Float): FloatArray {
        val x0 = applyX(x, y)
        val y0 = applyY(x, y)
        val x1 = applyX(x + w, y)
        val y1 = applyY(x + w, y)
        val x2 = applyX(x + w, y + h)
        val y2 = applyY(x + w, y + h)
        val x3 = applyX(x, y + h)
        val y3 = applyY(x, y + h)

        val minX = minOf(minOf(x0, x1), minOf(x2, x3))
        val minY = minOf(minOf(y0, y1), minOf(y2, y3))
        val maxX = maxOf(maxOf(x0, x1), maxOf(x2, x3))
        val maxY = maxOf(maxOf(y0, y1), maxOf(y2, y3))

        val outW = maxX - minX
        val outH = maxY - minY
        // 近似缩放因子：用 AABB 尺寸与原始尺寸的比值
        val sx = if (w > 0f) outW / w else 1f
        val sy = if (h > 0f) outH / h else 1f
        return floatArrayOf(minX, minY, outW, outH, sx, sy)
    }

    /**
     * 将屏幕坐标逆变换回元素局部坐标，判断是否在矩形内。
     *
     * @param mx 屏幕鼠标 X
     * @param my 屏幕鼠标 Y
     * @param x 元素局部矩形左上角 X
     * @param y 元素局部矩形左上角 Y
     * @param w 元素局部矩形宽度
     * @param h 元素局部矩形高度
     * @return 鼠标是否在变换后的矩形内
     */
    fun invertedHitTest(mx: Float, my: Float, x: Float, y: Float, w: Float, h: Float): Boolean {
        val inv = invert()
        val localX = inv.applyX(mx, my)
        val localY = inv.applyY(mx, my)
        val insideX = localX >= x && localX < x + w
        val insideY = localY >= y && localY < y + h
        return insideX && insideY
    }

    /** 导出为行优先 9 元素 float 数组，供 UiRenderBridge.pushTransform3x3 使用。 */
    fun toArray(): FloatArray = floatArrayOf(m00, m01, m02, m10, m11, m12, m20, m21, m22)

    companion object {
        /** 单位矩阵。 */
        @JvmField
        val IDENTITY = AffineMatrix3(
            1f, 0f, 0f,
            0f, 1f, 0f,
            0f, 0f, 1f,
        )

        /** 行列式接近零的阈值，低于此值视为退化矩阵。 */
        private const val DETERMINANT_EPSILON = 1e-6f

        /** 创建平移矩阵。 */
        @JvmStatic
        fun translate(tx: Float, ty: Float): AffineMatrix3 = AffineMatrix3(
            1f, 0f, tx,
            0f, 1f, ty,
            0f, 0f, 1f,
        )

        /** 创建缩放矩阵。 */
        @JvmStatic
        fun scale(sx: Float, sy: Float): AffineMatrix3 = AffineMatrix3(
            sx, 0f, 0f,
            0f, sy, 0f,
            0f, 0f, 1f,
        )

        /** 创建旋转矩阵（弧度制）。 */
        @JvmStatic
        fun rotate(rad: Float): AffineMatrix3 {
            val c = cos(rad.toDouble()).toFloat()
            val s = sin(rad.toDouble()).toFloat()
            return AffineMatrix3(
                c, -s, 0f,
                s, c, 0f,
                0f, 0f, 1f,
            )
        }

        /** 创建倾斜矩阵。 */
        @JvmStatic
        fun skew(sx: Float, sy: Float): AffineMatrix3 = AffineMatrix3(
            1f, tan(sx.toDouble()).toFloat(), 0f,
            tan(sy.toDouble()).toFloat(), 1f, 0f,
            0f, 0f, 1f,
        )

        /**
         * 从 UIElement 的 runtime 变换属性构建局部 3×3 矩阵（不含布局位置偏移）。
         *
         * perspective 不合成进矩阵——2D 平面上它不改变坐标，仅在 GPU 端 4×4 升维时体现。
         */
        @JvmStatic
        fun fromElement(el: UIElement<*>): AffineMatrix3 {
            var m = IDENTITY
            if (el.translateX() != 0f || el.translateY() != 0f) {
                m = m.multiply(translate(el.translateX(), el.translateY()))
            }
            if (el.scaleX() != 1f || el.scaleY() != 1f) {
                m = m.multiply(scale(el.scaleX(), el.scaleY()))
            }
            if (el.rotation() != 0f) {
                m = m.multiply(rotate(el.rotation()))
            }
            if (el.skewX() != 0f || el.skewY() != 0f) {
                m = m.multiply(skew(el.skewX(), el.skewY()))
            }
            return m
        }
    }
}
